/**
 * /cookies — Política de cookies.
 *
 * AlphaConcreto actually uses `localStorage` (not cookies) for state
 * persistence, but the page still explains the equivalent
 * "almacenamiento del navegador" so the user understands what gets saved.
 */

import { LegalSection, LegalShell } from '@/components/layout/LegalShell';

export function CookiesPage() {
  return (
    <LegalShell eyebrow="Legal" title="Política de cookies y almacenamiento" updated="Última actualización: mayo de 2026">
      <LegalSection title="¿Qué almacenamos en tu navegador?">
        <p>
          AlphaConcreto utiliza <strong>localStorage</strong> del navegador (no cookies de
          seguimiento de terceros). Estos datos se guardan únicamente en tu dispositivo y nunca se
          comparten con anunciantes ni redes sociales.
        </p>
      </LegalSection>

      <LegalSection title="Inventario de almacenamiento">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}>
                <th className="py-2 pr-3">Clave</th>
                <th className="py-2 pr-3">Propósito</th>
                <th className="py-2 pr-3">Caduca</th>
              </tr>
            </thead>
            <tbody>
              <Row k="alphaconcreto-auth" purpose="Token JWT de sesión + datos básicos del usuario." expires="Cuando cierras sesión." />
              <Row k="alphaconcreto-cart" purpose="Items que has añadido al carrito de compra." expires="Cuando vacías el carrito o pagas." />
              <Row k="alphaconcreto.onboarded.v1" purpose="Indica si ya viste el tutorial inicial." expires="Permanente (puedes borrarlo desde tu navegador)." />
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title="Cookies de terceros">
        <p>
          Cuando procesas un pago, el modal de <strong>Culqi</strong> puede instalar sus propias
          cookies para prevenir fraude. Estas cookies son gestionadas por Culqi bajo su propia
          política de privacidad: consulta{' '}
          <a href="https://www.culqi.com/privacidad" target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
            culqi.com/privacidad
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="¿Cómo eliminar este almacenamiento?">
        <p>Puedes borrarlo en cualquier momento:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Chrome / Edge:</strong> F12 → Application → Local Storage → Clear.</li>
          <li><strong>Firefox:</strong> F12 → Storage → Local Storage → Clear.</li>
          <li><strong>Safari:</strong> Develop → Show Web Inspector → Storage → Local Storage.</li>
          <li>O simplemente: cerrar sesión + cierra el carrito + vaciar caché.</li>
        </ul>
        <p className="mt-2 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
          Borrar el almacenamiento te cerrará sesión y vaciará tu carrito, pero no afecta tus datos
          guardados en nuestros servidores ni tu saldo de fierros.
        </p>
      </LegalSection>

      <LegalSection title="No utilizamos">
        <ul className="list-disc space-y-1 pl-5">
          <li>Cookies de seguimiento (Facebook Pixel, Google Analytics, Hotjar, etc.).</li>
          <li>Cookies publicitarias.</li>
          <li>Fingerprinting de dispositivo.</li>
        </ul>
      </LegalSection>
    </LegalShell>
  );
}

function Row({ k, purpose, expires }: { k: string; purpose: string; expires: string }) {
  return (
    <tr className="border-b align-top" style={{ borderColor: 'var(--border-subtle)' }}>
      <td className="py-2 pr-3 font-mono tabular text-[12px]" style={{ color: 'var(--accent)' }}>{k}</td>
      <td className="py-2 pr-3" style={{ color: 'var(--text-secondary)' }}>{purpose}</td>
      <td className="py-2 pr-3 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{expires}</td>
    </tr>
  );
}
